/**
 * Antes do assistente: por qual entidade o inscrito vai se inscrever (D-13,
 * RN-CP-20) — e se há período aberto para isso (RN-CP-03).
 *
 * O SGTP devolve os perfis do CPF da sessão: a própria pessoa física, as
 * empresas e a cooperativa que ele representa. **Uma inscrição por entidade**:
 * outra entidade é outra inscrição, e cada cartão diz a situação da sua.
 *
 * - DISPONÍVEL → começa uma inscrição nova;
 * - RASCUNHO → continua de onde parou (RN-CP-26: retomar é entrar e continuar);
 * - INSCRITA → já enviou neste período (RN-CP-28): só acompanha. O cartão leva
 *   ao comprovante.
 *
 * O cartão inteiro é o alvo do clique (regra 9 de interface).
 */

import { Building2, CalendarX2, ChevronRight, FileCheck2, User, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { Spinner } from '@/ui/Spinner';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { cn } from '@/nucleo/utilitarios';

import {
  mensagemDoErro,
  usePerfisDoInscrito,
  type PerfilDoInscrito,
  type TipoDeEntidade,
} from './apiDaInscricao';
import { formatarCnpj } from './contrato';

const ICONE: Record<TipoDeEntidade, typeof User> = {
  PF: User,
  PJ: Building2,
  COOPERATIVA: Users,
};

const ROTULO_DO_TIPO: Record<TipoDeEntidade, string> = {
  PF: 'Pessoa física',
  PJ: 'Pessoa jurídica',
  COOPERATIVA: 'Cooperativa',
};

const ACAO: Record<PerfilDoInscrito['situacao'], string> = {
  DISPONIVEL: 'Começar a inscrição',
  RASCUNHO: 'Continuar de onde parei',
  INSCRITA: 'Ver o comprovante',
};

export interface EscolhaDaEntidadeProps {
  aoEscolher: (perfil: PerfilDoInscrito) => void;
  /** Perfil cuja inscrição está sendo aberta agora (mostra espera no cartão). */
  abrindo: string | null;
  /** Recusa ao abrir (período fechado entre a leitura e o clique, entidade já inscrita). */
  aviso: { titulo: string; texto: string; protocolos?: string[] } | null;
}

export function EscolhaDaEntidade({ aoEscolher, abrindo, aviso }: EscolhaDaEntidadeProps) {
  const perfis = usePerfisDoInscrito();

  if (perfis.isPending) return <Spinner rotulo="Consultando o seu cadastro no SGTP" />;

  if (perfis.isError) {
    return (
      <ErrorState
        variant="load-failed"
        description={mensagemDoErro(
          perfis.error,
          'Não foi possível consultar o seu cadastro agora. Tente de novo em alguns minutos.',
        )}
        primaryAction={{ label: 'Tentar de novo', onClick: () => void perfis.refetch() }}
      />
    );
  }

  const { periodo, ciclo } = perfis.data;

  if (!periodo.aceitaInscricoes || !ciclo) {
    return (
      <EmptyState
        icon={CalendarX2}
        title="Não há período de inscrição aberto agora"
        description={periodo.mensagem}
      />
    );
  }

  if (perfis.data.perfis.length === 0) {
    return (
      <EmptyState
        icon={Building2}
        title="Nenhuma entidade no seu cadastro do SGTP"
        description="Para se inscrever, o seu CPF precisa estar vinculado a uma pessoa física, empresa ou cooperativa no SGTP. Procure o DTP para atualizar o cadastro."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold text-foreground">
          Por qual entidade você se inscreve?
        </h2>
        <p className="text-sm text-muted-foreground">
          Período <strong>{ciclo.nome}</strong>. Cada entidade faz a sua inscrição; os veículos
          escolhidos ganham um protocolo cada.
        </p>
      </div>

      {aviso && (
        <Alert>
          <FileCheck2 aria-hidden="true" className="h-4 w-4" />
          <AlertTitle>{aviso.titulo}</AlertTitle>
          <AlertDescription>
            <p>{aviso.texto}</p>
            {aviso.protocolos && aviso.protocolos.length > 0 && (
              <p className="mt-1 font-mono text-xs">{aviso.protocolos.join(' · ')}</p>
            )}
          </AlertDescription>
        </Alert>
      )}

      <ul className="space-y-3">
        {perfis.data.perfis.map((perfil) => {
          const Icone = ICONE[perfil.tipo];
          const conteudo = (
            <>
              <Icone aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <span className="min-w-0 flex-1 space-y-1">
                <span className="block font-semibold text-foreground">{perfil.nome}</span>
                <span className="block text-xs text-muted-foreground">
                  {ROTULO_DO_TIPO[perfil.tipo]} ·{' '}
                  <span className="font-mono">{formatarCnpj(perfil.documento)}</span>
                </span>
                {perfil.situacao === 'INSCRITA' && perfil.protocolos.length > 0 && (
                  <span className="block font-mono text-xs text-muted-foreground">
                    {perfil.protocolos.join(' · ')}
                  </span>
                )}
                <span className="block text-sm font-medium text-primary">
                  {abrindo === perfil.chave ? 'Abrindo…' : ACAO[perfil.situacao]}
                </span>
              </span>
              {perfil.situacao !== 'DISPONIVEL' && (
                <Badge variant="outline" className="shrink-0">
                  {perfil.situacao === 'RASCUNHO' ? 'Em preenchimento' : 'Inscrita'}
                </Badge>
              )}
              <ChevronRight aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
            </>
          );
          const classe =
            'flex w-full items-start gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:bg-muted/40 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring';

          // Inscrita com protocolo da própria conta: leva ao comprovante. Sem
          // protocolo (inscrita por outro representante da mesma entidade),
          // não há o que abrir aqui — o cartão só informa.
          if (perfil.situacao === 'INSCRITA') {
            const primeiro = perfil.protocolos[0];
            return (
              <li key={perfil.chave}>
                {primeiro ? (
                  <Link to={`/credenciados/enviada/${primeiro}`} className={classe}>
                    {conteudo}
                  </Link>
                ) : (
                  <div className={cn(classe, 'hover:bg-card')}>
                    <Icone aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    <span className="min-w-0 flex-1 space-y-1">
                      <span className="block font-semibold text-foreground">{perfil.nome}</span>
                      <span className="block text-sm text-muted-foreground">
                        Esta entidade já tem inscrição neste período, feita por outro representante.
                        Acompanhe com ele pelo protocolo.
                      </span>
                    </span>
                  </div>
                )}
              </li>
            );
          }

          return (
            <li key={perfil.chave}>
              <button
                type="button"
                className={classe}
                disabled={abrindo !== null}
                onClick={() => aoEscolher(perfil)}
              >
                {conteudo}
              </button>
            </li>
          );
        })}
      </ul>

      <p className="text-sm text-muted-foreground">
        Já enviou?{' '}
        <Button asChild variant="link" className="h-auto p-0">
          <Link to="/credenciados/acompanhamento">Acompanhe as suas inscrições</Link>
        </Button>
      </p>
    </div>
  );
}

export default EscolhaDaEntidade;
